// The Stock & Dispatch overview (V4 sd-module overview; handoff §7n.1a). The
// API counts every tile and chooses every row (GET /api/stock/overview, in
// the tenant's zone); this file only words a due date and says where each
// figure leads.

import { fmtDay } from './model'

export interface OverviewTile {
  key: string
  label: string
  value: number
  sub: string
}

export interface OverviewDue {
  id: string
  reference: string
  customer: string | null
  destination: string | null
  due: string | null
  days: number | null
  dispatched: number
  ordered: number
  approval_status: string
  approval_label: string
}

export interface OverviewLoad {
  id: string
  reference: string
  date: string | null
  time: string | null
  days: number | null
  carrier: string | null
  orders: string | null
  pallets: number
  verified: number
  state: string
  state_label: string
}

export interface OverviewException {
  kind: 'order' | 'pallet' | 'load'
  id: string
  ref: string
  what: string
}

export interface Overview {
  today: string
  horizon: string
  tiles: OverviewTile[]
  due: OverviewDue[]
  loads: OverviewLoad[]
  exceptions: OverviewException[]
}

// Where each tile opens, already filtered to what it counts (V4 tiles' go).
export const TILE_TO: Record<string, string> = {
  available: '/stock-dispatch',
  held: '/stock-dispatch?hold=held',
  shortages: '/stock-dispatch/orders?view=active&progress=short',
  approval: '/stock-dispatch/orders?view=awaiting',
  preparation: '/stock-dispatch?tab=to',
  loads_today: '/stock-dispatch/dispatches',
  discrepancies: '/stock-dispatch?tab=pallets&state=discrepancy',
  invoice: '/stock-dispatch/orders?view=all&progress=invoice',
}

export const orderTo = (id: string) => '/stock-dispatch/orders?open=' + encodeURIComponent(id)
export const loadTo = (id: string) => '/stock-dispatch/dispatches?open=' + encodeURIComponent(id)
export const palletTo = (id: string) => '/stock-dispatch?tab=pallets&open=' + encodeURIComponent(id)

export const exceptionTo = (e: OverviewException) => (e.kind === 'order' ? orderTo(e.id) : e.kind === 'load' ? loadTo(e.id) : palletTo(e.id))

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// V4 fmtD: "Tue 23 Sep", read from the date's own parts.
export function fmtWeekday(d: string | null | undefined): string {
  const m = d && /^(\d{4})-(\d{2})-(\d{2})/.exec(d)
  if (!m) return 'Unscheduled'
  const day = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay()
  return DAYS[day] + ' ' + fmtDay(d)
}

export type DueTone = 'late' | 'today' | 'ahead' | 'unsched'

// V4 dueChip: late in red with how late, today in ink, ahead muted, and no
// date at all as Unscheduled. `days` is the API's, counted from its today.
export function dueWhen(date: string | null, days: number | null): { label: string; tone: DueTone } {
  if (!date || days == null) return { label: 'Unscheduled', tone: 'unsched' }
  if (days < 0) return { label: fmtDay(date) + ' · ' + -days + (days === -1 ? ' day late' : ' days late'), tone: 'late' }
  if (days === 0) return { label: 'Today', tone: 'today' }
  if (days === 1) return { label: 'Tomorrow', tone: 'ahead' }
  return { label: fmtWeekday(date), tone: 'ahead' }
}
